// Page PUBLIQUE d'un questionnaire d'étude (062) : la personne y répond sans
// compte, avec le lien personnel reçu par e-mail, ou le lien commun d'un baromètre.
// Le jeton de l'URL est la seule autorisation. Chaque question est facultative.
import { useEffect, useState } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Eyebrow, Spinner } from '../components/ui';
import { LogoMark, Wordmark } from '../components/Logo';
import { LegalFooter } from '../legal/LegalPages';
import { useAnswerQuestionnaire, usePublicStudyQuestionnaire, type PublicQuestion } from './questionnaire';
import './studies.css';

type Answer = string | number | string[];

function Question({ q, value, onChange }: { q: PublicQuestion; value: Answer | undefined; onChange: (v: Answer | undefined) => void }) {
  const id = `q-${q.key}`;
  if (q.type === 'ouverte') {
    return (
      <textarea id={id} className="zy-input" rows={3} value={String(value ?? '')} onChange={(e) => onChange(e.target.value || undefined)} />
    );
  }
  if (q.type === 'echelle') {
    const min = q.min ?? 1;
    const max = q.max ?? 5;
    return (
      <div className="pq-scale" role="radiogroup" aria-labelledby={`${id}-label`}>
        {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
          <button key={n} type="button" role="radio" aria-checked={value === n}
            className={`pq-scale-n${value === n ? ' pq-on' : ''}`} onClick={() => onChange(value === n ? undefined : n)}>
            {n}
          </button>
        ))}
      </div>
    );
  }
  const options = q.type === 'oui_non' ? ['oui', 'non'] : q.options;
  const multi = q.type === 'choix_multiple';
  const picked = multi ? (Array.isArray(value) ? value : []) : value;
  return (
    <div className="pq-options" role={multi ? 'group' : 'radiogroup'} aria-labelledby={`${id}-label`}>
      {options.map((o) => {
        const on = multi ? (picked as string[]).includes(o) : picked === o;
        return (
          <label key={o} className={`pq-option${on ? ' pq-on' : ''}`}>
            <input className={multi ? 'zy-check' : 'zy-radio'}
              type={multi ? 'checkbox' : 'radio'}
              name={id}
              checked={on}
              onChange={() => {
                if (!multi) return onChange(o);
                const list = picked as string[];
                const next = on ? list.filter((x) => x !== o) : [...list, o];
                onChange(next.length ? next : undefined);
              }}
            />
            {q.type === 'oui_non' ? (o === 'oui' ? 'Oui' : 'Non') : o}
          </label>
        );
      })}
    </div>
  );
}

export function PublicStudyQuestionnaire({ token }: { token: string }) {
  const query = usePublicStudyQuestionnaire(token);
  const answer = useAnswerQuestionnaire(token);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});

  // Le jeton est dans l'URL : il ne part pas en Referer.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  if (query.isPending) return <div className="app-loading"><Spinner label="Ouverture du questionnaire…" /></div>;
  const q = query.data?.questionnaire;
  const set = (key: string, v: Answer | undefined) =>
    setAnswers((a) => { const n = { ...a }; if (v === undefined) delete n[key]; else n[key] = v; return n; });

  // Les questions groupées par section, dans l'ordre.
  const sections: { title: string; questions: PublicQuestion[] }[] = [];
  for (const item of q?.questions ?? []) {
    const last = sections.at(-1);
    if (last && last.title === item.section) last.questions.push(item);
    else sections.push({ title: item.section, questions: [item] });
  }
  const done = answer.isSuccess || q?.answered;
  const greeting = q?.name ? `Bonjour ${q.name}. ` : '';

  return (
    <main className="auth pq-page">
      <div className="auth-card pq-card">
        <div className="auth-brand">
          <LogoMark size={32} />
          <Wordmark className="auth-brand-name" />
        </div>
        {!q ? (
          <>
            <h1 className="t-h2">Questionnaire indisponible</h1>
            <p className="auth-lede">
              {query.error instanceof ApiRequestError && query.error.status === 404
                ? 'Ce lien n’est plus valide. Demandez-en un nouveau à la personne qui vous l’a envoyé.'
                : 'Le serveur est momentanément injoignable. Réessayez dans un instant.'}
            </p>
          </>
        ) : q.closed && !done ? (
          <>
            <h1 className="t-h2">Ce baromètre est clos</h1>
            <p className="auth-lede">Il ne reçoit plus de réponses. Merci de votre aide !</p>
          </>
        ) : done ? (
          <div className="pq-thanks" role="status">
            <span className="pq-thanks-icon" aria-hidden="true">🙏</span>
            <h1 className="t-h2">{q.name ? `Merci, ${q.name} !` : 'Merci !'}</h1>
            <p className="auth-lede">{q.closing || 'Vos réponses sont bien arrivées. Elles aideront à améliorer le produit.'}</p>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); answer.mutate(answers); }}>
            <Eyebrow>Questionnaire{q.organization ? ` · ${q.organization}` : ''}</Eyebrow>
            <h1 className="t-h2">{q.title}</h1>
            <p className="auth-lede">
              {greeting}{q.intro || `${q.organizer ?? 'Une équipe de recherche'} vous remercie de prendre quelques minutes.`} Chaque question est facultative.
            </p>
            {sections.map((s) => (
              <fieldset key={s.title + s.questions[0]!.key} className="pq-section">
                {s.title ? <legend className="pq-section-title">{s.title}</legend> : null}
                {s.questions.map((item) => (
                  <div key={item.key} className="pq-question">
                    <p className="pq-question-text" id={`q-${item.key}-label`}>
                      <label htmlFor={`q-${item.key}`}>{item.text}</label>
                    </p>
                    <Question q={item} value={answers[item.key]} onChange={(v) => set(item.key, v)} />
                  </div>
                ))}
              </fieldset>
            ))}
            {answer.error ? <ErrorBanner>{answer.error instanceof ApiRequestError ? answer.error.message : 'L’envoi a échoué.'}</ErrorBanner> : null}
            <Button type="submit" variant="accent" loading={answer.isPending} disabled={Object.keys(answers).length === 0}>
              Envoyer mes réponses
            </Button>
            <p className="auth-notice">
              {q.anonymous
                ? 'Vos réponses sont enregistrées sans votre nom : personne ne peut les relier à vous, et elles ne peuvent donc plus être retrouvées ni retirées ensuite.'
                : 'Vos réponses ne servent qu’à cette étude. Pour les faire supprimer, écrivez à la personne qui vous a envoyé ce questionnaire.'}
            </p>
          </form>
        )}
        <LegalFooter compact />
      </div>
    </main>
  );
}
